'use client';

import { Link } from '@/core/i18n/navigation';
import { cn } from '@/shared/lib/utils';
import { Section } from '@/shared/types/blocks/landing';

export function Content({
  section,
  className,
}: {
  section: Section;
  className?: string;
}) {
  if (!section.sections?.length) {
    return null;
  }

  return (
    <section
      id={section.id}
      className={cn('border-y border-border/60 bg-background py-16 md:py-24', section.className, className)}
    >
      <div className="container">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          {section.label && <p className="section-label">{section.label}</p>}
          {section.title && <h2 className="section-title text-foreground">{section.title}</h2>}
          {section.description && (
            <p className="mt-4 text-lg font-light leading-relaxed text-muted-foreground">
              {section.description}
            </p>
          )}
        </div>

        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
          {section.sections.map((item: Section, index: number) => (
            <article
              key={`${item.title}-${index}`}
              className="rounded-3xl border border-border/70 bg-card/60 p-6 shadow-sm backdrop-blur-sm"
            >
              <div className="mb-4 text-xs uppercase tracking-[0.24em] text-primary">
                {String(index + 1).padStart(2, '0')}
              </div>
              <h3 className="mb-4 font-display text-2xl font-semibold tracking-tight text-foreground">
                {item.title}
              </h3>
              <p className="text-base font-light leading-8 text-muted-foreground">
                {item.content}
              </p>
              {item.link && item.link_text && (
                <Link
                  href={item.link}
                  className="mt-6 inline-flex text-sm font-medium text-primary underline decoration-current underline-offset-4 transition-colors hover:text-primary/80"
                >
                  {item.link_text}
                </Link>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
